import qs from "qs";

import consumerApi from "api/services/consumer-api";
import { Coordinates } from "custom-types/Coordinates";
import logError from "utils/logError";

import { Product } from "../../custom-types/Product";

export type ProductFilter = {
  key: string;
  options: Array<{ key: string; docCount: number }>;
};

type ProductsResponse = {
  data: Product[];
  metadata: {
    totalCount: number;
    availableFilters: ProductFilter[];
  };
};

export type SortValue = {
  [key: string]: "asc" | "desc";
};

export type SortFilter = {
  [key: number]: SortValue;
};

type SearchProps = {
  filter: {
    available_nearby?: boolean;
    brands?: string[];
    category?: string;
    max_distance?: string;
    pickup_only?: boolean;
    strain_categories?: string[];
    strains?: string[];
    subcategory?: string;
  };
  skip?: number;
  sort?: SortFilter;
  take?: number;
};

const defaultCoordinates = {
  lat: 47.6062,
  lon: -122.3321,
};

const searchProducts = async (
  options: SearchProps,
  countryCode = "US",
  coordinates: Coordinates = defaultCoordinates,
): Promise<{
  availableFilters: ProductFilter[];
  products: Product[];
  totalCount: number;
}> => {
  const defaultOptions = {
    filter: {
      available_nearby: false,
      max_distance: "100mi",
      pickup_only: false,
    },
    lat: coordinates.lat,
    lon: coordinates.lon,
    skip: 0,
    sort: { 0: { recommended: "desc" } },
    take: 12,
  };

  try {
    const response = await consumerApi.get<ProductsResponse>(
      `/api/products/v2/search?${qs.stringify(
        { ...defaultOptions, ...options },
        {
          arrayFormat: "brackets",
        },
      )}`,
      {
        headers: {
          "X-Country-Code": countryCode.toUpperCase(),
        },
      },
    );

    return {
      availableFilters: response.data.metadata.availableFilters,
      products: response.data.data,
      totalCount: response.data.metadata.totalCount,
    };
  } catch (e) {
    logError(e.message, {
      functionName: "searchProducts",
      service: "consumer",
      statusCode: e.statusCode,
    });

    return {
      availableFilters: [],
      products: [],
      totalCount: 0,
    };
  }
};

export default searchProducts;
